/**
 * 74.13 step 3 (DECISIONS 369, 374 — the owner took all eight leans of ARCHITECTURE §5.47; 376 read the sheet): mountains as
 * landforms. A pass over a generated planet that gives it the kinds of mountain its world makes, placed by the system and
 * eroded, behind the Lab's Landforms row (`MatchSetup.landforms`, off by default: off, nothing here runs). Prototyped on
 * design_sheets/mountains.html (step 2), which now draws this module. Pure: dmath, fixed orders, + − × and floor in the noise.
 *
 * The pass, on the world sheet's weld (core/planetRoutes.ts: ~10k unique vertices on a megaplanet, CSR edges):
 * 1. **The axis first** (374 (6)): the spin axis from the base field, as generated — the kinds read latitude, and
 *    `withLandforms` keeps the base field's key (`baseGroundKey`) so the axis stays put however the landforms are tuned.
 * 2. **Uplift fields** (374 (1)): a few plates as nearest-seed cells on the sphere, each with a drift; where two converge,
 *    a band of uplift along their edge, its distance warped by noise so the range bends — the fold ranges. A world without
 *    the kind gets none.
 * 3. **The stamped kinds** (374 (1), (2)), by the archetype, the air and the climate: volcanic cones at hotspots (tephra), a
 *    caprock's mesas and buttes (the dry worlds), impact massifs (the airless), cirques cut into the cold heights (where the
 *    climate's temperature at the summit is cold: bruma everywhere, a temperate world toward its poles) leaving horns and
 *    arêtes between them.
 * 4. **Stream-power erosion** on the landform layer (Braun & Willett's implicit step, n = 1): the vertices in ascending
 *    height, each one's receiver its lowest neighbour, the drainage area accumulated downhill, each cut by K·A^m toward its
 *    receiver while the uplift raises it — dendritic valleys, knife ridges between, passes where valleys meet. The base
 *    field is never cut below itself (the plains the owner likes stay), and an airless world is not eroded.
 * 5. **Thermal**: what stands past the talus grade slides down, except a caprock's rim and a cirque's headwall (they stay
 *    cliffs); where it lands is scree (374 (5): a material, at step 4).
 * 5½. **Erosion carving** (74.19, DECISIONS 435; `MatchSetup.erosion`, light by default on the action clock at a person's scale since 441): core/erosionCarve.ts on the ground as
 *    it stands (base + layer), holding the rims, headwalls and scree; its change kept apart from the layer and laid on both
 *    the mesh and step 6's radii.
 * 6. **The features**: the cliffs (mesh edges past the walk's 1.2 — the faces step 4 lays as Shaping ribbons, 374 (3)), the
 *    scree, the passes (the world sheet's saddles on the ranges). Nothing reads them in play yet (step 4).
 *
 * On today's mesh (376: a finer one is measured later): a megaplanet's facet is ~43 people, so a mountain is a few large
 * triangles; the Story's large world (facets ~10 people) takes lower, wider ranges, and its crests and cliffs come from the
 * fine layer at step 4 (376).
 */
import type { PlanetArchetype, PlanetState, Shaping, Vec3 } from '../types';
import { planetRouteGraph, type PlanetRouteGraph } from './planetRoutes';
import { isSaddle } from './worldSheet';
import { erosionCut, type ErosionDial } from './erosionCarve';
import { groundKey, spinAxisOf } from './spinAxis';
import { temperatureAt, ARCHETYPE_TEMPERATURE, CLIMATE } from './climate';
import { airOf } from './air';
import { dacos, dcos, dsin, dpow } from './dmath';
import { SeededRandom } from './random';
import { MAX_TERRAIN_HEIGHT_FRACTION, MAX_CRATER_DEPTH_FRACTION } from '../constants/physics';

export type LandformKind = 'fold' | 'cone' | 'mesa' | 'cirque' | 'massif';

/** Which kinds a world gives (374 (2): the archetype, the air and the climate; cirques also on any world's cold heights). */
export const KINDS_BY_ARCHETYPE: Record<PlanetArchetype, LandformKind[]> = {
  thalia: ['fold'], viridis: ['fold'], murex: ['fold'], pelago: ['fold'], bruma: ['fold'],
  tephra: ['cone', 'fold'], ochrus: ['mesa'], sonora: ['mesa'], pallor: ['massif'],
};

/** The dials (angles in radians on the sphere, heights as shares of the radius, as the generator's own; design_sheets/mountains.html shows them). */
export const LANDFORMS = {
  plates: 9,
  /** sizes are angles with a floor in facets (a world of detail 4 has facets twice as wide as a megaplanet's), heights a target
   * flank grade over the size, capped as a share of R — so a range reads as sharp on the Story's world as on a megaplanet */
  /** a fold range: its half-width about the plate edge (or this many facets), its flank grade, the cap, the edge's warp; only edges closing this fast */
  foldHalf: 0.1, foldHalfFacets: 3.2, foldGrade: 1.3, foldCap: 0.2, coarseFacet: 0.05, coarseFoldFacets: 4.5, coarseFoldCap: 0.1, foldWarp: 0.09, foldClosing: 0.35,
  /** volcanic cones: how many, base radius (≥ facets), flank grade, cap, the summit crater (share of the base radius, depth share of the height) */
  cones: [3, 6] as [number, number], coneR: [0.07, 0.13] as [number, number], coneFacets: 2.5, coneGrade: [1.0, 1.35] as [number, number], coneCap: 0.22, craterShare: 0.16, craterDepth: 0.18,
  /** mesas and buttes: count, radius (≥ facets), caprock height (one or two tiers; at least a cliff's drop over a facet) */
  mesas: [6, 12] as [number, number], mesaR: [0.035, 0.11] as [number, number], mesaFacets: 1.5, mesaH: [0.03, 0.06] as [number, number], mesaCliff: 1.5, mesaCap: 0.15, buttes: [6, 14] as [number, number],
  /** impact massifs: count, radius, rim height, floor depth, central peak (shares of the rim height) */
  massifs: [4, 8] as [number, number], massifR: [0.08, 0.18] as [number, number], massifFacets: 3, rimH: [0.04, 0.08] as [number, number], rimFacets: 1.2,
  /** cirques: the summit must be this cold (the climate's temperature), bowls a peak, bowl radius and depth (share of the peak's own relief) */
  // (read at use: core/climate sits on an import cycle back through mapgen to here, so at this module's load CLIMATE may not
  // be initialised yet — design_sheets/craterlet_look.html threw "Cannot access 'CLIMATE' before initialization")
  get cirqueCold(): number { return CLIMATE.coldAt; },
  cirqueAir: 0.3, bowls: 3, bowlR: 0.045, bowlFacets: 1.5, bowlDepth: 0.55, bowlOff: 0.04, cirquePeaks: 14,
  /** stream-power: iterations, K, m; uplift per iteration */
  spIters: 70, spK: 0.42, spM: 0.5,
  /** thermal: the talus grade (rise over run, world units), iterations, the share moved a pass; scree from this deposit (share of R) */
  talus: 1.6, thermalIters: 24, thermalRate: 0.22, screeFrom: 0.0012,
  /** the walk's limit: a mesh edge past it is a cliff (374 (3): laid as a Shaping ribbon at step 4) */
  cliffGrade: 1.2,
  /**
   * step 4, the cliff bands (374 (3)): along a cliff's rim a `raise` ribbon — a shelf at the rim's level, then a face at
   * `bandFace` (rise over run) down to where it meets the mesh's slope, `bandShare` of the facet's run out from the rim; a
   * rim already steeper than `bandSteepest` × the face keeps the mesh's own; and only along the contour, an edge between two
   * rims whose own grade is under `bandAlong` (on a steep range nearly every vertex is a rim: banded down the slope as well,
   * thalia's 278 bands were a lattice of raised ribbons on the mesh's triangles, 184 of them down the slope — the coordinator, Oct 30)
   * 74.15 (DECISIONS 388, the walls: lane E's mountain style sheet): `bandAlong` 0.45 (it was 0.3); a gap of up to
   * `wallBridge` vertices along the contour between rims becomes rim when it is on a face itself (its own steepest drop
   * past `wallDrop` × `cliffGrade`) and not a pass; a chain of bands under `wallKeep` segments is dropped
   */
  bandFace: 4, bandShare: 0.5, bandSteepest: 0.8, bandAlong: 0.45, wallBridge: 2, wallKeep: 3, wallDrop: 0.6,
  /**
   * step 4, the crests on a world under `crestBelowR` (376: the Story's large world's crests from the fine layer — its ranges
   * tens of people high, a facet 8–10 people, where a megaplanet's are hundreds): along a range's spine a
   * `raise` ribbon `crestLift` of a facet's run over the mesh's crest, `crestHalf` of a run across, its flanks at
   * `crestFlank` down to the mesh; a spine vertex in the top `crestFrom` share of its range's added height, flanks no
   * steeper than `crestSteepest` × the flank (a steeper crest is sharp already)
   */
  crestBelowR: 30, crestLift: 0.45, crestHalf: 0.04, crestFlank: 1.6, crestFrom: 0.12, crestSteepest: 0.9,
  /** how far past where a record's batter meets the mesh's slope its reach runs (the mesh is not a plane: no step at the reach) */
  reachSlack: 1.5,
};

export interface Landforms {
  /** the new heights, one per mesh corner (the planet's heightmap's layout) */
  heightmap: Float32Array;
  /** per weld vertex: the landform layer added (world units), which kind made it (−1 none), scree, a pass */
  added: Float32Array; kind: Int8Array; scree: Uint8Array; pass: Uint8Array;
  /** cliff edges (weld vertex pairs) and the crest vertices */
  cliffs: Array<[number, number]>;
  /** where the most landform stands (a unit direction), for the sheet's camera */
  focus: Vec3;
  kinds: LandformKind[];
  /** the cold summits that took cirques */
  cirques: number;
  /** step 4: the cliff bands (and on a large world the crests) as ground records, ascending ids from 1 (`PlanetState.shapings`) */
  shapings: Shaping[];
  /** step 4: per facet of the heightmap (corner index / 3), 1 where two of its corners took scree (`PlanetState.screeFacets`); null where none did */
  screeFacets: Uint8Array | null;
  /** the weld the pass ran on (the generated planet's) */
  graph: PlanetRouteGraph;
  /** 74.19: the erosion carving's change per weld vertex (world units, kept apart from `added`), null with the dial off */
  carve: Float64Array | null;
  /** 74.19's yards: with the carve, the ground and records without it (the dial off's, to the bit) — the world setup plans on; null with the dial off */
  bare: { heightmap: Float32Array; shapings: Shaping[]; screeFacets: Uint8Array | null } | null;
  /** per record in `shapings` (by index): the weld vertices whose radii are its levels — 74.19's yards never move them */
  levelVerts: number[][];
}

const KIND_INDEX: Record<LandformKind, number> = { fold: 0, cone: 1, mesa: 2, cirque: 3, massif: 4 };
export const KIND_NAMES: LandformKind[] = ['fold', 'cone', 'mesa', 'cirque', 'massif'];

// --- lattice value noise (+ − × floor only, Math.imul hashing: the same on every engine) ---
function hash3(x: number, y: number, z: number, s: number): number {
  let h = Math.imul(x, 0x8da6b343) ^ Math.imul(y, 0xd8163841) ^ Math.imul(z, 0xcb1ab31f) ^ Math.imul(s, 0x27d4eb2f);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b); h ^= h >>> 13;
  return ((h >>> 0) / 4294967296) * 2 - 1;
}
const sm = (t: number) => t * t * (3 - 2 * t);
export function vnoise(x: number, y: number, z: number, s: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z), fx = sm(x - xi), fy = sm(y - yi), fz = sm(z - zi);
  const l = (a: number, b: number, t: number) => a + (b - a) * t;
  const c = (dx: number, dy: number, dz: number) => hash3(xi + dx, yi + dy, zi + dz, s);
  return l(l(l(c(0, 0, 0), c(1, 0, 0), fx), l(c(0, 1, 0), c(1, 1, 0), fx), fy), l(l(c(0, 0, 1), c(1, 0, 1), fx), l(c(0, 1, 1), c(1, 1, 1), fx), fy), fz);
}
function fbm(x: number, y: number, z: number, s: number, oct: number): number {
  let a = 0.5, f = 1, t = 0;
  for (let o = 0; o < oct; o++) { t += a * vnoise(x * f, y * f, z * f, s + o * 101); a *= 0.5; f *= 2.03; }
  return t;
}

const angle = (d: Float64Array, v: number, c: Vec3) => dacos(Math.max(-1, Math.min(1, d[3 * v] * c.x + d[3 * v + 1] * c.y + d[3 * v + 2] * c.z)));
function randomDir(rng: SeededRandom): Vec3 {
  const th = rng.range(0, Math.PI * 2), z = rng.range(-1, 1), r = Math.sqrt(1 - z * z);
  return { x: r * dcos(th), y: r * dsin(th), z };
}

/**
 * The landform pass over a generated planet (its `initialHeightmap` and mesh, the archetype, the air, the spin axis): the
 * new heights and the features. Pure: a function of the planet as generated, seeded by its ground key, so the same world
 * always takes the same landforms (and the Lab's row, the sheet and a replay agree).
 */
export function landformsOf(planet: PlanetState, erosion?: ErosionDial): Landforms {
  const g = planetRouteGraph(planet), V = g.count, d = g.dirs, R = planet.radius;
  const base = new Float64Array(V);
  for (let v = 0; v < V; v++) base[v] = g.radii[v] - R;
  // the mesh's facet as an angle (the mean edge): every size below has a floor in facets
  let fsum = 0, fcount = 0;
  for (let v = 0; v < V; v += 7) for (let k = g.start[v]; k < g.start[v + 1]; k++) { const n = g.nbr[k]; fsum += dacos(Math.max(-1, Math.min(1, d[3 * v] * d[3 * n] + d[3 * v + 1] * d[3 * n + 1] + d[3 * v + 2] * d[3 * n + 2]))); fcount++; }
  const fa = fsum / Math.max(1, fcount), atLeast = (x: number, facets: number) => Math.max(x, facets * fa);
  // (a coarse mesh — the Story's large world, detail 4 — takes lower, wider ranges: a crest of a few vertices is a pyramid)
  const coarse = fa > LANDFORMS.coarseFacet;
  const foldHalf = atLeast(LANDFORMS.foldHalf, coarse ? LANDFORMS.coarseFoldFacets : LANDFORMS.foldHalfFacets), foldHeight = Math.min(coarse ? LANDFORMS.coarseFoldCap : LANDFORMS.foldCap, LANDFORMS.foldGrade * foldHalf);
  const arch = (planet.archetype ?? 'thalia') as PlanetArchetype;
  const kinds = [...(KINDS_BY_ARCHETYPE[arch] ?? ['fold'])];
  const air = airOf(planet).density;
  const rng = new SeededRandom(`${groundKey(planet)}-landforms`);
  // 1. the axis from the base field (the generated planet as it stands, before any landform)
  const axis = spinAxisOf(planet);
  const lat = new Float64Array(V);
  for (let v = 0; v < V; v++) lat[v] = Math.PI / 2 - angle(d, v, axis);
  const U = new Float64Array(V), stamp = new Float64Array(V), kind = new Int8Array(V).fill(-1), fixed = new Uint8Array(V);
  const mark = (v: number, k: LandformKind, amount: number, into: Float64Array) => {
    if (amount <= 0) return;
    if (into === U ? amount > U[v] * 0.5 : Math.abs(amount) > Math.abs(stamp[v]) * 0.5) kind[v] = KIND_INDEX[k];
  };
  // 2. plates and the fold ranges along their converging edges
  if (kinds.includes('fold')) {
    const P = LANDFORMS.plates, centres: Vec3[] = [], drift: Vec3[] = [];
    for (let p = 0; p < P; p++) { centres.push(randomDir(rng)); drift.push(randomDir(rng)); }
    const strength = kinds[0] === 'fold' ? 1 : 0.45;   // (tephra: the cones first, a weaker range)
    for (let v = 0; v < V; v++) {
      const x = d[3 * v], y = d[3 * v + 1], z = d[3 * v + 2];
      // a warp of the point, so the plate edges (and the ranges on them) bend
      const w = LANDFORMS.foldWarp;
      const wx = x + w * fbm(x * 3, y * 3, z * 3, 11, 3), wy = y + w * fbm(x * 3 + 7, y * 3, z * 3, 12, 3), wz = z + w * fbm(x * 3, y * 3 + 5, z * 3, 13, 3);
      const wl = Math.sqrt(wx * wx + wy * wy + wz * wz), q = { x: wx / wl, y: wy / wl, z: wz / wl };
      let a = -1, b = -1, da = 9, db = 9;
      for (let p = 0; p < P; p++) {
        const c = centres[p], dd = dacos(Math.max(-1, Math.min(1, q.x * c.x + q.y * c.y + q.z * c.z)));
        if (dd < da) { db = da; b = a; da = dd; a = p; } else if (dd < db) { db = dd; b = p; }
      }
      // closing speed: the two drifts' difference along the line between the plates' centres
      const ca = centres[a], cb = centres[b], ax = cb.x - ca.x, ay = cb.y - ca.y, az = cb.z - ca.z, al = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
      const closing = ((drift[a].x - drift[b].x) * ax + (drift[a].y - drift[b].y) * ay + (drift[a].z - drift[b].z) * az) / al;
      if (closing <= LANDFORMS.foldClosing) continue;
      const edge = (db - da) / 2, h = foldHalf;
      if (edge > h) continue;
      const t = 1 - edge / h, up = strength * Math.min(1, closing) * t * t * (3 - 2 * t) * (0.65 + 0.35 * fbm(x * 9, y * 9, z * 9, 21, 3));
      U[v] = Math.max(U[v], up); mark(v, 'fold', up, U);
    }
  }
  // 3. the stamps
  const radial = (c: Vec3, r: number, f: (s: number) => number, k: LandformKind, fix?: (s: number) => boolean) => {
    for (let v = 0; v < V; v++) {
      const a = angle(d, v, c);
      if (a >= r * 1.6) continue;
      const amount = f(a / r);
      if (amount === 0) continue;
      stamp[v] = Math.abs(amount) > Math.abs(stamp[v]) ? amount : stamp[v];
      mark(v, k, Math.abs(amount), stamp);
      if (fix && fix(a / r)) fixed[v] = 1;
    }
  };
  if (kinds.includes('cone')) {
    const n = rng.int(LANDFORMS.cones[0], LANDFORMS.cones[1]);
    let at = randomDir(rng);
    for (let i = 0; i < n; i++) {
      // a chain: each cone a step from the last, now and then a jump to a new hotspot
      if (i > 0 && rng.next() < 0.65) { const s = randomDir(rng), m = 0.22; at = norm({ x: at.x + s.x * m, y: at.y + s.y * m, z: at.z + s.z * m }); } else if (i > 0) at = randomDir(rng);
      const r = atLeast(rng.range(LANDFORMS.coneR[0], LANDFORMS.coneR[1]), LANDFORMS.coneFacets), H = Math.min(LANDFORMS.coneCap, r * rng.range(LANDFORMS.coneGrade[0], LANDFORMS.coneGrade[1])) * R;
      const crater = LANDFORMS.craterShare, depth = LANDFORMS.craterDepth;
      radial(at, r, s => s >= 1 ? 0 : s < crater ? H * (1 - depth * (1 - (s / crater) * (s / crater))) : H * dpow(1 - (s - crater) / (1 - crater), 1.5), 'cone', s => s < crater * 1.3);
    }
  }
  if (kinds.includes('mesa')) {
    // the caprock: a flat top, its outline warped, a rim (kept a cliff), the talus left to the thermal pass; buttes are small mesas
    const n = rng.int(LANDFORMS.mesas[0], LANDFORMS.mesas[1]), nb = rng.int(LANDFORMS.buttes[0], LANDFORMS.buttes[1]);
    for (let i = 0; i < n + nb; i++) {
      const c = randomDir(rng), butte = i >= n;
      const r = butte ? atLeast(rng.range(0.012, 0.028), 0.9) : atLeast(rng.range(LANDFORMS.mesaR[0], LANDFORMS.mesaR[1]), LANDFORMS.mesaFacets);
      const H = Math.min(LANDFORMS.mesaCap, Math.max(rng.range(LANDFORMS.mesaH[0], LANDFORMS.mesaH[1]), LANDFORMS.mesaCliff * fa) * (butte ? 1.2 : 1)) * R, tiers = !butte && rng.next() < 0.4, s0 = i * 37;
      // 74.15 (a): the caprock is level — one top for the whole cap (its ground's mean + H, and never under its highest
      // corner + H/2), the bench one under it. A stamp of H over the ground as it lay left the ground's own swell on the
      // top: the coarse mesh's peak came up through the bands' flat shelves round the rim (the slab with the peak through it)
      const cap: number[] = [], bench: number[] = [];
      let sum = 0, hi = -Infinity;
      for (let v = 0; v < V; v++) {
        const a = angle(d, v, c);
        if (a >= r * 1.8) continue;
        const x = d[3 * v], y = d[3 * v + 1], z = d[3 * v + 2];
        const edge = r * (1 + 0.32 * fbm(x * 40, y * 40, z * 40, 300 + s0, 3));   // the outline, ragged
        if (a < edge) { cap.push(v); sum += base[v]; hi = Math.max(hi, base[v]); if (a > edge * 0.86) fixed[v] = 1; }   // (the caprock's rim stays a cliff)
        else if (tiers && a < edge * 1.35) bench.push(v);   // a lower bench
      }
      if (!cap.length) continue;
      const top = Math.max(sum / cap.length + H, hi + H * 0.5), shelf = top - H * 0.45;
      for (const v of cap) {
        const h = top - base[v];
        if (h > stamp[v]) { stamp[v] = h; kind[v] = KIND_INDEX.mesa; }
        fixed[v] = 1;   // (the whole cap: the thermal pass leaves a level top level)
      }
      for (const v of bench) { const h = shelf - base[v]; if (h > stamp[v]) { stamp[v] = h; kind[v] = KIND_INDEX.mesa; } }
    }
  }
  if (kinds.includes('massif')) {
    const n = rng.int(LANDFORMS.massifs[0], LANDFORMS.massifs[1]);
    for (let i = 0; i < n; i++) {
      const c = randomDir(rng), r = atLeast(rng.range(LANDFORMS.massifR[0], LANDFORMS.massifR[1]), LANDFORMS.massifFacets), H = Math.max(rng.range(LANDFORMS.rimH[0], LANDFORMS.rimH[1]), LANDFORMS.rimFacets * fa) * R;
      // floor below the rim, a sharp rim (steep inside, gentle outside: the ejecta), a central peak on the larger ones
      radial(c, r, s => {
        const floor = s < 0.85 ? -0.5 * H * (1 - (s / 0.85) * (s / 0.85)) : 0;
        const rim = s < 1 ? H * dpow(Math.max(0, (s - 0.5) / 0.5), 2.2) : H * dpow(Math.max(0, 1 - (s - 1) / 0.6), 2);
        const peak = r > 0.12 && s < 0.18 ? 0.9 * H * (1 - s / 0.18) : 0;
        return floor + rim + peak;
      }, 'massif', s => s > 0.92 && s < 1.02);
    }
  }
  // 4. stream-power on the landform layer: the stamps are its start, the folds its uplift
  const L = new Float64Array(V);
  for (let v = 0; v < V; v++) L[v] = stamp[v];
  const order = new Int32Array(V), area = new Float64Array(V), recv = new Int32Array(V), dist = new Float64Array(V);
  const H = new Float64Array(V);
  const eroded = air > 0.05;   // (an airless world: no water, no fluvial cut)
  const uplift = foldHeight * R / LANDFORMS.spIters * 2.2;
  for (let it = 0; it < (eroded || kinds.includes('fold') ? LANDFORMS.spIters : 0); it++) {
    for (let v = 0; v < V; v++) { H[v] = base[v] + L[v]; order[v] = v; }
    order.sort((a, b) => H[a] - H[b] || a - b);
    for (let v = 0; v < V; v++) {
      let lo = -1, lh = H[v];
      for (let k = g.start[v]; k < g.start[v + 1]; k++) { const n = g.nbr[k]; if (H[n] < lh || (H[n] === lh && n < v && lo < 0)) { lh = H[n]; lo = n; } }
      recv[v] = lo; area[v] = 1;
      if (lo >= 0) { const dx = d[3 * v] - d[3 * lo], dy = d[3 * v + 1] - d[3 * lo + 1], dz = d[3 * v + 2] - d[3 * lo + 2]; dist[v] = Math.sqrt(dx * dx + dy * dy + dz * dz) * R; }
    }
    for (let i = V - 1; i >= 0; i--) { const v = order[i]; if (recv[v] >= 0) area[recv[v]] += area[v]; }
    for (let i = 0; i < V; i++) {
      const v = order[i], r = recv[v];
      const up = U[v] * uplift;
      // (the caprock resists: mesas and massifs keep their stamped form, only thermal works their edges)
      if (r < 0 || !eroded || fixed[v] || (U[v] === 0 && kind[v] !== KIND_INDEX.cone)) { L[v] += up; continue; }
      const f = LANDFORMS.spK * dpow(area[v], LANDFORMS.spM) / dist[v];
      const Hn = (H[v] + up + f * (base[r] + L[r])) / (1 + f);
      L[v] = Math.max(0, Hn - base[v]);
    }
  }
  // the folds to their height: the layer's tallest fold vertex at foldHeight (the erosion shaped it, this sets its scale)
  let foldMax = 0;
  for (let v = 0; v < V; v++) if (kind[v] === KIND_INDEX.fold && L[v] > foldMax) foldMax = L[v];
  if (foldMax > 0) { const s = foldHeight * R / foldMax; for (let v = 0; v < V; v++) if (kind[v] === KIND_INDEX.fold) L[v] *= s; else if (U[v] > 0) L[v] *= s; }
  // 3b. cirques on the cold summits (after the ranges stand: they cut the heads of the valleys), the horn left between bowls
  let cirques = 0;
  const tBase = ARCHETYPE_TEMPERATURE[arch] ?? 0.55;
  // (ice needs air, and a dry world's cold heights stay rock: no cirques on the airless or where the caprock is the kind)
  if (air >= LANDFORMS.cirqueAir && !kinds.includes('mesa')) {
    for (let v = 0; v < V; v++) H[v] = base[v] + L[v];
    let lo = Infinity, hi = -Infinity;
    for (let v = 0; v < V; v++) { if (H[v] < lo) lo = H[v]; if (H[v] > hi) hi = H[v]; }
    const peaks: number[] = [];
    for (let v = 0; v < V; v++) {
      if (L[v] < 0.03 * R) continue;
      let top = true;
      for (let k = g.start[v]; k < g.start[v + 1] && top; k++) if (H[g.nbr[k]] >= H[v]) top = false;
      if (!top) continue;
      const elev = (H[v] - lo) / (hi - lo || 1);
      if (temperatureAt(tBase, lat[v], elev, air) <= LANDFORMS.cirqueCold) peaks.push(v);
    }
    peaks.sort((a, b) => H[b] - H[a] || a - b);
    for (const pv of peaks.slice(0, LANDFORMS.cirquePeaks)) {
      cirques++;
      const p = { x: d[3 * pv], y: d[3 * pv + 1], z: d[3 * pv + 2] };
      const ref = Math.abs(p.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
      const e1 = norm(cross(ref, p)), e2 = cross(p, e1), turn = rng.range(0, Math.PI * 2);
      const relief = L[pv];
      for (let b = 0; b < LANDFORMS.bowls; b++) {
        const th = turn + (b / LANDFORMS.bowls) * Math.PI * 2, o = atLeast(LANDFORMS.bowlOff, LANDFORMS.bowlFacets * 0.9);
        const c = norm({ x: p.x + o * (dcos(th) * e1.x + dsin(th) * e2.x), y: p.y + o * (dcos(th) * e1.y + dsin(th) * e2.y), z: p.z + o * (dcos(th) * e1.z + dsin(th) * e2.z) });
        const r = atLeast(LANDFORMS.bowlR, LANDFORMS.bowlFacets), D = LANDFORMS.bowlDepth * relief;
        for (let v = 0; v < V; v++) {
          const a = angle(d, v, c);
          if (a >= r) continue;
          // a bowl: deepest a little downhill of its centre, its headwall (toward the peak) steep
          const s = a / r, cut = D * (1 - s * s) * (1 - s * s);
          if (cut > 0.002 * R) {
            const lv = Math.max(0, L[v] - cut);
            if (lv < L[v]) { L[v] = lv; kind[v] = KIND_INDEX.cirque; if (s > 0.7) fixed[v] = 1; }
          }
        }
      }
    }
  }
  // 5. thermal: past the talus grade the ground slides (rims and headwalls kept); the deposit is scree
  const dep = new Float64Array(V);
  for (let it = 0; it < LANDFORMS.thermalIters; it++) {
    for (let v = 0; v < V; v++) H[v] = base[v] + L[v];
    for (let v = 0; v < V; v++) {
      if (fixed[v] || L[v] <= 0) continue;
      for (let k = g.start[v]; k < g.start[v + 1]; k++) {
        const n = g.nbr[k], dx = d[3 * v] - d[3 * n], dy = d[3 * v + 1] - d[3 * n + 1], dz = d[3 * v + 2] - d[3 * n + 2];
        const run = Math.sqrt(dx * dx + dy * dy + dz * dz) * R, drop = H[v] - H[n], excess = drop - LANDFORMS.talus * run;
        if (excess <= 0) continue;
        const m = Math.min(L[v], excess * LANDFORMS.thermalRate * 0.5);
        L[v] -= m; L[n] += m; dep[n] += m; H[v] -= m; H[n] += m;
      }
    }
  }
  // 5½. 74.19 (DECISIONS 435; lane C's hook): erosion carving on the ground as it stands — the base field and the layer
  // together — holding the caprock rims and cirque headwalls (`fixed`) and the scree ground (thermal's deposit), its change
  // kept apart from the layer (so step 6's features on the layer read as before) and laid on both the mesh and step 6's
  // radii, so the records' levels equal the carved mesh. None when the dial is off or the world is airless (as step 4).
  let carve: Float64Array | null = null;
  if (erosion !== undefined && erosion !== 'off') {
    const here = new Float64Array(V), hold = new Uint8Array(V);
    for (let v = 0; v < V; v++) { here[v] = R + base[v] + L[v]; if (fixed[v] || (dep[v] > LANDFORMS.screeFrom * R && L[v] > 0)) hold[v] = 1; }
    carve = erosionCut(planet, g, here, erosion, hold)?.delta ?? null;
  }
  // the result, clamped as the generator clamps; step 6 on the ground without the carve and (the dial on) on the carved ground
  const added = new Float32Array(V);
  for (let v = 0; v < V; v++) added[v] = L[v];
  for (const p of KIND_NAMES) if (!kinds.includes(p) && p === 'cirque' && cirques > 0) kinds.push('cirque');
  const finish = (cv: Float64Array | null) => groundAndFeatures(planet, g, base, L, added, dep, cv);
  const bare = finish(null), carved = carve ? finish(carve) : null, top = carved ?? bare;
  let fv = 0;
  for (let v = 1; v < V; v++) if (L[v] > L[fv]) fv = v;
  const { levelVerts: _lv, ...topOut } = top;
  return { ...topOut, added, kind, focus: { x: d[3 * fv], y: d[3 * fv + 1], z: d[3 * fv + 2] }, kinds, cirques, graph: g, carve,
    bare: carved ? { heightmap: bare.heightmap, shapings: bare.shapings, screeFacets: bare.screeFacets } : null, levelVerts: top.levelVerts };
}

/**
 * Step 6 on one ground: the heights (the generated ones plus the landform layer `L` plus the carve `cv`, clamped as the
 * generator clamps) and the features read from them — scree, passes, cliff edges, the records (their levels the mesh's own
 * rim, to the bit) and the scree facets. 74.19's yards: run without the carve for the world setup plans on, and with it for
 * the ground the carve record lays (core/carveYards.ts).
 */
function groundAndFeatures(planet: PlanetState, g: PlanetRouteGraph, base: Float64Array, L: Float64Array, added: Float32Array, dep: Float64Array, carve: Float64Array | null) {
  const V = g.count, d = g.dirs, R = planet.radius;
  const maxUp = R * MAX_TERRAIN_HEIGHT_FRACTION, maxDown = -R * MAX_CRATER_DEPTH_FRACTION;
  const heightmap = new Float32Array(planet.heightmap.length);
  const ini = planet.initialHeightmap ?? planet.heightmap;
  if (carve) for (let i = 0; i < heightmap.length; i++) heightmap[i] = Math.max(maxDown, Math.min(maxUp, ini[i] + added[g.nodeOf[i]] + carve[g.nodeOf[i]]));
  else for (let i = 0; i < heightmap.length; i++) heightmap[i] = Math.max(maxDown, Math.min(maxUp, ini[i] + added[g.nodeOf[i]]));
  // 6. the features
  const radiusNew = new Float64Array(V);
  if (carve) for (let v = 0; v < V; v++) radiusNew[v] = R + Math.max(maxDown, Math.min(maxUp, base[v] + L[v] + carve[v]));
  else for (let v = 0; v < V; v++) radiusNew[v] = R + Math.max(maxDown, Math.min(maxUp, base[v] + L[v]));
  const scree = new Uint8Array(V), pass = new Uint8Array(V), cliffs: Array<[number, number]> = [];
  for (let v = 0; v < V; v++) if (dep[v] > LANDFORMS.screeFrom * R && L[v] > 0) scree[v] = 1;
  for (let v = 0; v < V; v++) {
    if (L[v] > 0.02 * R && isSaddle(g, radiusNew, v)) pass[v] = 1;
    for (let k = g.start[v]; k < g.start[v + 1]; k++) {
      const n = g.nbr[k];
      if (n <= v) continue;
      const dx = d[3 * v] - d[3 * n], dy = d[3 * v + 1] - d[3 * n + 1], dz = d[3 * v + 2] - d[3 * n + 2], run = Math.sqrt(dx * dx + dy * dy + dz * dz) * R;
      if (Math.abs(radiusNew[v] - radiusNew[n]) > LANDFORMS.cliffGrade * run && (L[v] > 0 || L[n] > 0)) cliffs.push([v, n]);
    }
  }
  // (the records' levels from the stored heights: a band's level is the mesh's own rim, to the bit)
  const radiusMesh = new Float64Array(V);
  for (let i = 0; i < heightmap.length; i++) radiusMesh[g.nodeOf[i]] = R + heightmap[i];
  const levelVerts: number[][] = [];
  const shapings = featureShapings(g, R, radiusMesh, cliffs, L, pass, R < LANDFORMS.crestBelowR, levelVerts);
  // the scree as a material by facet (374 (5)): a facet two of whose corners took the thermal pass's deposit
  let screeFacets: Uint8Array | null = null;
  for (let t = 0; t * 3 + 2 < heightmap.length; t++) {
    const n = scree[g.nodeOf[t * 3]] + scree[g.nodeOf[t * 3 + 1]] + scree[g.nodeOf[t * 3 + 2]];
    if (n >= 2) { if (!screeFacets) screeFacets = new Uint8Array(heightmap.length / 3); screeFacets[t] = 1; }
  }
  return { heightmap, scree, pass, cliffs, shapings, screeFacets, levelVerts };
}

/**
 * Step 4 (374 (3), 376): the person-scale features as ground records (core/shaping.ts's rule; core/shapingGround.ts reads them).
 * - **Cliff bands**: the rim of each cliff edge (its upper vertex) joined to the rims beside it along the contour (`bandAlong`),
 *   a 2-node `raise` ribbon a segment, its levels the rims' radii, laid on the feet's side of the rim line only (74.15 (b)). Below the rim the ground is held up to a shelf, then falls at `bandFace`: the
 *   mesh's soft face over a facet becomes a band of cliff in its upper part, the slope below untouched.
 * - **Crests** (a world under `crestBelowR` only: the Story's large world, 376): a range's spine — a vertex higher than all its neighbours but two,
 *   in the top of its range — joined along it, a `raise` ribbon lifted `crestLift` of a run over the mesh, flanks at
 *   `crestFlank`: a knife ridge on the soft swell the mesh carries.
 * Each record's reach runs past where its batter meets the mesh's slope (`reachSlack`), so the ground has no step.
 */
function featureShapings(g: PlanetRouteGraph, R: number, radius: Float64Array, cliffs: ReadonlyArray<[number, number]>, L: Float64Array, pass: Uint8Array, crests: boolean, levelVerts?: number[][]): Shaping[] {
  const V = g.count, d = g.dirs, out: Shaping[] = [];
  const dirOf = (v: number): Vec3 => ({ x: d[3 * v], y: d[3 * v + 1], z: d[3 * v + 2] });
  const runOf = (a: number, b: number) => { const dx = d[3 * a] - d[3 * b], dy = d[3 * a + 1] - d[3 * b + 1], dz = d[3 * a + 2] - d[3 * b + 2]; return Math.sqrt(dx * dx + dy * dy + dz * dz) * R; };
  // the rims: each cliff edge's upper vertex, with its steepest cliff grade and that edge's run
  const rimGrade = new Float64Array(V), rimRun = new Float64Array(V);
  for (const [a, b] of cliffs) {
    const top = radius[a] >= radius[b] ? a : b, foot = top === a ? b : a, run = runOf(a, b), s = (radius[top] - radius[foot]) / run;
    if (s > rimGrade[top]) { rimGrade[top] = s; rimRun[top] = run; }
  }
  const F = LANDFORMS.bandFace;
  const onContour = (a: number, b: number) => Math.abs(radius[a] - radius[b]) < LANDFORMS.bandAlong * runOf(a, b);   // (along the rim, not down the slope)
  // 74.15 (388) the walls: a gap of a vertex or two along the contour between rims becomes rim — on a face itself, and never
  // at a pass (the walls break there)
  for (let it = 0; it < LANDFORMS.wallBridge; it++) {
    const add: Array<[number, number, number]> = [];
    for (let x = 0; x < V; x++) {
      if (rimGrade[x] > 0 || pass[x]) continue;
      let a = -1, b = -1, far = -1, n = 0;
      for (let k = g.start[x]; k < g.start[x + 1]; k++) { const u = g.nbr[k]; if (rimGrade[u] > 0 && onContour(u, x)) n++; }
      if (n < 2) continue;
      // the two rims most apart: x lies between them, not beside one
      for (let i = g.start[x]; i < g.start[x + 1]; i++) {
        const u = g.nbr[i];
        if (!(rimGrade[u] > 0) || !onContour(u, x)) continue;
        for (let j = i + 1; j < g.start[x + 1]; j++) {
          const v = g.nbr[j];
          if (!(rimGrade[v] > 0) || !onContour(v, x)) continue;
          const r = runOf(u, v);
          if (r > far) { far = r; a = u; b = v; }
        }
      }
      if (far < 1.4 * Math.min(runOf(a, x), runOf(x, b))) continue;
      let steepest = 0;
      for (let k = g.start[x]; k < g.start[x + 1]; k++) { const u = g.nbr[k]; steepest = Math.max(steepest, (radius[x] - radius[u]) / runOf(x, u)); }
      if (steepest < LANDFORMS.wallDrop * LANDFORMS.cliffGrade) continue;   // (a mesa's top inside its rim is not a face)
      add.push([x, (rimGrade[a] + rimGrade[b]) / 2, (rimRun[a] + rimRun[b]) / 2]);
    }
    for (const [x, sg, rn] of add) { rimGrade[x] = sg; rimRun[x] = rn; }
  }
  const pairs: Array<[number, number]> = [];
  for (let a = 0; a < V; a++) {
    if (!(rimGrade[a] > 0)) continue;
    for (let k = g.start[a]; k < g.start[a + 1]; k++) {
      const b = g.nbr[k];
      if (b <= a || !(rimGrade[b] > 0)) continue;
      if ((rimGrade[a] + rimGrade[b]) / 2 >= LANDFORMS.bandSteepest * F) continue;   // (steep at the mesh's own scale already)
      if (onContour(a, b)) pairs.push([a, b]);
    }
  }
  // the stubs go: a wall keeps `wallKeep` segments or more (its chain: the bands joined at their rims)
  const chain = new Int32Array(V).fill(-1), size: number[] = [], adj = new Map<number, number[]>();
  for (const [a, b] of pairs) { (adj.get(a) ?? adj.set(a, []).get(a)!).push(b); (adj.get(b) ?? adj.set(b, []).get(b)!).push(a); }
  for (const [v0] of pairs) {
    if (chain[v0] >= 0) continue;
    const id = size.length, st = [v0];
    chain[v0] = id; let e = 0;
    while (st.length) { const u = st.pop()!; for (const w of adj.get(u)!) { e++; if (chain[w] < 0) { chain[w] = id; st.push(w); } } }
    size.push(e / 2);
  }
  for (const [a, b] of pairs) {
    if (size[chain[a]] < LANDFORMS.wallKeep) continue;
    const s = (rimGrade[a] + rimGrade[b]) / 2, run = (rimRun[a] + rimRun[b]) / 2;
    // the shelf w out from the rim, then the face at F until it meets the slope s: x_meet = F·w / (F − s) = bandShare of the run
    const meet = LANDFORMS.bandShare * run, w = meet * (F - s) / F;
    // 74.15 (b): the shelf on the feet's side of the rim only — the ribbon w/2 wide, its middle w/2 out from the rim line
    // toward the feet. Centred on the rim it reached w inward too, and over a ridge or a cap that raised the far side to
    // the rim's level: a flat slab with the summit coming up through it
    // (the feet's side: the lower of the two triangles on the rim's edge — their third corners, one either side of it)
    const pa = dirOf(a), pb = dirOf(b);
    let ox = pa.y * pb.z - pa.z * pb.y, oy = pa.z * pb.x - pa.x * pb.z, oz = pa.x * pb.y - pa.y * pb.x;   // across the rim line
    const ol = Math.sqrt(ox * ox + oy * oy + oz * oz) || 1;
    let low = -1;
    for (let i = g.start[a]; i < g.start[a + 1]; i++) {
      const c = g.nbr[i];
      if (c === b || (low >= 0 && radius[c] >= radius[low])) continue;
      for (let j = g.start[b]; j < g.start[b + 1]; j++) if (g.nbr[j] === c) { low = c; break; }
    }
    const toward = low < 0 ? 1 : d[3 * low] * ox + d[3 * low + 1] * oy + d[3 * low + 2] * oz;
    const sh = (toward < 0 ? -1 : 1) * (w / 2) / (R * ol);
    ox *= sh; oy *= sh; oz *= sh;
    const out1 = (p: Vec3): Vec3 => { const x = p.x + ox, y = p.y + oy, z = p.z + oz, l = Math.sqrt(x * x + y * y + z * z); return { x: x / l, y: y / l, z: z / l }; };
    levelVerts?.push([a, b]);   // (74.19's yards: the vertices whose radii are its levels)
    out.push({ id: out.length + 1, mode: 'raise', cut: F, fill: F, reach: (meet - w) * LANDFORMS.reachSlack,
      footprint: { kind: 'ribbon', half: w / 2, nodes: [{ dir: out1(pa), level: radius[a] }, { dir: out1(pb), level: radius[b] }] } });
  }
  if (crests) {
    // the spine: a vertex higher than all its neighbours but two (its ridge's way on), in the top of the range's added height
    let top = 0;
    for (let v = 0; v < V; v++) if (L[v] > top) top = L[v];
    const spine = new Uint8Array(V), flank = new Float64Array(V), run = new Float64Array(V);
    for (let v = 0; v < V; v++) {
      if (!(top > 0) || L[v] < LANDFORMS.crestFrom * top) continue;
      let higher = 0, steepest = 0, rs = 0, n = 0;
      for (let k = g.start[v]; k < g.start[v + 1]; k++) {
        const u = g.nbr[k], r = runOf(v, u);
        if (radius[u] >= radius[v]) higher++;
        steepest = Math.max(steepest, (radius[v] - radius[u]) / r); rs += r; n++;
      }
      if (higher <= 2 && steepest < LANDFORMS.crestSteepest * LANDFORMS.crestFlank) { spine[v] = 1; flank[v] = steepest; run[v] = rs / Math.max(1, n); }
    }
    const Fc = LANDFORMS.crestFlank;
    for (let a = 0; a < V; a++) {
      if (!spine[a]) continue;
      for (let k = g.start[a]; k < g.start[a + 1]; k++) {
        const b = g.nbr[k];
        if (b <= a || !spine[b]) continue;
        const r = (run[a] + run[b]) / 2, lift = LANDFORMS.crestLift * r, s = Math.max(flank[a], flank[b]);
        // the flank at Fc from lift over the crest meets the mesh's slope s at lift / (Fc − s) out
        levelVerts?.push([a, b]);
        out.push({ id: out.length + 1, mode: 'raise', cut: Fc, fill: Fc, reach: (lift / (Fc - s)) * LANDFORMS.reachSlack,
          footprint: { kind: 'ribbon', half: LANDFORMS.crestHalf * r, nodes: [{ dir: dirOf(a), level: radius[a] + lift }, { dir: dirOf(b), level: radius[b] + lift }] } });
      }
    }
  }
  return out;
}

/**
 * The planet with its landforms (GameConfig's generation, `MatchSetup.landforms`: core/mapgen.ts generateMap runs it on every
 * planet before anything stands on the ground). The heights as generated become the landforms' (`initialHeightmap` too: the
 * material bands and the strata read them), and the base field's key is kept (`baseGroundKey`) so the spin axis and the world
 * sheet's seeded sites hang on the world, not on the landforms' dials (374 (6)).
 */
export function withLandforms(planet: PlanetState, erosion?: ErosionDial): PlanetState {
  const lf = landformsOf(planet, erosion);
  return { ...planet, heightmap: lf.heightmap, initialHeightmap: lf.heightmap, baseGroundKey: groundKey(planet), ...(lf.shapings.length ? { shapings: lf.shapings } : {}), ...(lf.screeFacets ? { screeFacets: lf.screeFacets } : {}) };
}

/**
 * 74.19's yards (DECISIONS 438): the planet a match's setup plans on — the landforms without the carve, the dial off's
 * planet to the bit — with the carved ground and its records kept aside (`carved`) for the setup's last step to lay, its
 * yards held (core/carveYards.ts layCarve). The dial off, or nothing carved: withLandforms' planet.
 */
export function plannedLandforms(planet: PlanetState, erosion?: ErosionDial): PlanetState {
  const lf = landformsOf(planet, erosion);
  if (!lf.bare) return { ...planet, heightmap: lf.heightmap, initialHeightmap: lf.heightmap, baseGroundKey: groundKey(planet), ...(lf.shapings.length ? { shapings: lf.shapings } : {}), ...(lf.screeFacets ? { screeFacets: lf.screeFacets } : {}) };
  const b = lf.bare;
  return { ...planet, heightmap: b.heightmap, initialHeightmap: b.heightmap, baseGroundKey: groundKey(planet), ...(b.shapings.length ? { shapings: b.shapings } : {}), ...(b.screeFacets ? { screeFacets: b.screeFacets } : {}),
    carved: { heightmap: lf.heightmap, shapings: lf.shapings, screeFacets: lf.screeFacets ?? undefined, levelVerts: lf.levelVerts } };
}

function norm(v: Vec3): Vec3 { const l = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; }
function cross(a: Vec3, b: Vec3): Vec3 { return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x }; }

/** The slope figures of a heightmap over the weld (rise over run per edge), as the survey reads them (the sheet's table, the tests). */
export function slopeStats(g: PlanetRouteGraph, R: number, heights: Float32Array, only?: Float32Array): { p50: number; p95: number; over06: number; over12: number; maxPeople: number } {
  const V = g.count, h = new Float64Array(V);
  for (let i = 0; i < heights.length; i++) h[g.nodeOf[i]] = heights[i];
  const s: number[] = [];
  let mx = -Infinity;
  for (let v = 0; v < V; v++) {
    if (h[v] > mx) mx = h[v];
    for (let k = g.start[v]; k < g.start[v + 1]; k++) {
      const n = g.nbr[k]; if (n <= v) continue;
      if (only && !(only[v] > 0 || only[n] > 0)) continue;   // (the landforms' own edges)
      const dx = g.dirs[3 * v] - g.dirs[3 * n], dy = g.dirs[3 * v + 1] - g.dirs[3 * n + 1], dz = g.dirs[3 * v + 2] - g.dirs[3 * n + 2];
      s.push(Math.abs(h[v] - h[n]) / (Math.sqrt(dx * dx + dy * dy + dz * dz) * R));
    }
  }
  s.sort((a, b) => a - b);
  const q = (p: number) => s[Math.min(s.length - 1, Math.floor(p * s.length))];
  return { p50: q(0.5), p95: q(0.95), over06: s.filter(x => x > 0.6).length / s.length, over12: s.filter(x => x > 1.2).length / s.length, maxPeople: mx };
}
